<template>
  <div class="account-category-page">
    <div class="settings-card">
      <h2 class="page-title">
        <Icon icon="heroicons:tag" width="24" height="24" />
        账号分类管理
      </h2>

      <!-- 搜索区域 -->
      <div class="search-bar">
        <div class="search-left">
          <el-input v-model="searchForm.account_category" placeholder="请输入分类名称" clearable size="large"
            class="search-input" @keyup.enter="handleSearch">
            <template #prefix>
              <Icon icon="heroicons:magnifying-glass" width="18" height="18" />
            </template>
          </el-input>

          <el-button type="primary" size="large" @click="handleSearch" class="search-btn">
            <Icon icon="heroicons:magnifying-glass" width="18" height="18" />
            <span class="btn-text">搜索</span>
          </el-button>
        </div>

        <el-button type="success" size="large" @click="openAddDialog" class="add-btn">
          <Icon icon="heroicons:plus" width="18" height="18" />
          <span class="btn-text">新增分类</span>
        </el-button>
      </div>

      <!-- 分类列表 - 表格包装器实现横向滚动 -->
      <div class="table-wrapper">
        <el-table :data="categoryList" stripe v-loading="loading" class="category-table"
          :row-style="{ background: 'transparent' }" :cell-style="{ background: 'transparent' }"
          :header-cell-style="{ background: 'rgba(157, 78, 221, 0.15)', color: '#fff' }">
          <el-table-column prop="account_category" label="分类名称" width="200" />
          <el-table-column prop="ip_port" label="IP+端口" width="180">
            <template #default="scope">
              <span v-if="scope.row.ip_port" class="info-text">{{ scope.row.ip_port }}</span>
              <span v-else class="empty-text">-</span>
            </template>
          </el-table-column>
          <el-table-column prop="ip_password" label="账号密码" width="180">
            <template #default="scope">
              <span v-if="scope.row.ip_password" class="warning-text">{{ scope.row.ip_password }}</span>
              <span v-else class="empty-text">-</span>
            </template>
          </el-table-column>
          <el-table-column prop="notes" label="备注" min-width="150" show-overflow-tooltip>
            <template #default="scope">
              <span class="notes-text">{{ scope.row.notes || '-' }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="create_time" label="创建时间" width="180" />
          <el-table-column prop="update_time" label="更新时间" width="180" />

          <el-table-column label="操作" width="180" fixed="right">
            <template #default="scope">
              <button class="action-btn edit-btn" @click="openEditDialog(scope.row)">
                <Icon icon="heroicons:pencil-square" width="14" height="14" />
                <span class="btn-text">编辑</span>
              </button>
              <button class="action-btn delete-btn" @click="handleDelete(scope.row.id)">
                <Icon icon="heroicons:trash" width="14" height="14" />
                <span class="btn-text">删除</span>
              </button>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- 分页组件 -->
      <div class="pagination-container">
        <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
          :total="pagination.total" :page-sizes="[10, 20, 30, 50]" 
          :layout="paginationLayout"
          @size-change="fetchCategoryList" @current-change="fetchCategoryList" background />
      </div>
    </div>

    <!-- 新增/编辑分类对话框 -->
    <el-dialog v-model="dialogVisible" :title="dialogTitle" :width="dialogWidth" :close-on-click-modal="false"
      class="custom-dialog" :top="dialogTop">
      <el-form ref="categoryForm" :model="categoryForm" :label-width="formLabelWidth" :rules="formRules">
        <el-form-item label="分类名称" prop="account_category">
          <el-input v-model="categoryForm.account_category" placeholder="请输入分类名称" size="large" />
        </el-form-item>

        <el-form-item label="IP+端口">
          <el-input v-model="categoryForm.ip_port" placeholder="格式: 192.168.1.1:8080" size="large" />
          <div class="input-tip">格式: IP地址:端口号，如 192.168.1.1:8080</div>
        </el-form-item>

        <el-form-item label="账号密码">
          <el-input v-model="categoryForm.ip_password" placeholder="格式: 用户名:密码" size="large" />
          <div class="input-tip">格式: 账号:密码，如 admin:123456</div>
        </el-form-item>

        <el-form-item label="备注信息">
          <el-input v-model="categoryForm.notes" placeholder="请输入备注信息" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>

      <template #footer>
        <button class="dialog-btn cancel-btn" @click="dialogVisible = false">取消</button>
        <button class="dialog-btn confirm-btn" @click="submitCategoryForm">确认</button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import Cookies from 'js-cookie';
import { Icon } from '@iconify/vue';
import { ElMessage, ElMessageBox } from 'element-plus';

export default {
  name: 'AccountCategory',
  components: { Icon },
  data() {
    return {
      loading: false,
      dialogVisible: false,
      dialogTitle: '新增分类',
      isEditMode: false,
      searchForm: {
        account_category: ''
      },
      categoryForm: {
        id: null,
        account_category: '',
        ip_port: '',
        ip_password: '',
        notes: ''
      },
      formRules: {
        account_category: [
          { required: true, message: '请输入分类名称', trigger: 'blur' },
          { min: 2, max: 50, message: '长度在 2 到 50 个字符', trigger: 'blur' }
        ]
      },
      categoryList: [],
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      screenWidth: window.innerWidth
    }
  },
  computed: {
    userInfo() {
      return JSON.parse(Cookies.get('user_info') || '{}')
    },
    // 响应式分页布局
    paginationLayout() {
      if (this.screenWidth < 576) {
        return 'prev, pager, next'
      } else if (this.screenWidth < 768) {
        return 'total, prev, pager, next'
      } else {
        return 'total, sizes, prev, pager, next, jumper'
      }
    },
    // 响应式对话框宽度
    dialogWidth() {
      if (this.screenWidth < 576) {
        return '95%'
      } else if (this.screenWidth < 768) {
        return '90%'
      } else {
        return '600px'
      }
    },
    // 响应式对话框顶部距离
    dialogTop() {
      if (this.screenWidth < 576) {
        return '5vh'
      } else {
        return '10vh'
      }
    },
    // 响应式表单标签宽度
    formLabelWidth() {
      if (this.screenWidth < 576) {
        return '80px'
      } else {
        return '100px'
      }
    }
  },
  mounted() {
    this.fetchCategoryList();
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    handleResize() {
      this.screenWidth = window.innerWidth;
    },
    // 获取分类列表
    async fetchCategoryList() {
      this.loading = true;
      try {
        const params = {
          action: this.searchForm.account_category ? 'search' : 'list',
          store_id: this.userInfo.id,
          page: this.pagination.page,
          page_size: this.pagination.pageSize
        };

        if (this.searchForm.account_category) {
          params.account_category = this.searchForm.account_category;
        }

        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/account_category_management.php`,
          params
        );

        if (data.data) {
          this.categoryList = data.data;
          this.pagination.total = data.pagination?.total || data.data.length;
        } else {
          this.categoryList = [];
          this.pagination.total = 0;
        }
      } catch (error) {
        ElMessage.error('获取分类列表失败');
        console.error('获取分类列表错误:', error);
      } finally {
        this.loading = false;
      }
    },

    // 处理搜索
    handleSearch() {
      this.pagination.page = 1;
      this.fetchCategoryList();
    },

    // 打开新增对话框
    openAddDialog() {
      this.dialogTitle = '新增分类';
      this.isEditMode = false;
      this.categoryForm = {
        id: null,
        account_category: '',
        ip_port: '',
        ip_password: '',
        notes: ''
      };
      this.dialogVisible = true;
      this.$nextTick(() => {
        if (this.$refs.categoryForm) {
          this.$refs.categoryForm.clearValidate();
        }
      });
    },

    // 打开编辑对话框
    openEditDialog(row) {
      this.dialogTitle = '编辑分类';
      this.isEditMode = true;
      this.categoryForm = {
        id: row.id,
        account_category: row.account_category,
        ip_port: row.ip_port || '',
        ip_password: row.ip_password || '',
        notes: row.notes || ''
      };
      this.dialogVisible = true;
      this.$nextTick(() => {
        if (this.$refs.categoryForm) {
          this.$refs.categoryForm.clearValidate();
        }
      });
    },

    // 提交表单（新增/编辑）
    submitCategoryForm() {
      this.$refs.categoryForm.validate(async (valid) => {
        if (valid) {
          try {
            const action = this.isEditMode ? 'update' : 'add';
            const payload = {
              ...this.categoryForm,
              store_id: this.userInfo.id
            };

            const { data } = await axios.post(
              `${config.BASE_URL}/shop/api/account_category_management.php`,
              {
                action,
                ...payload
              }
            );

            if (data.success) {
              ElMessage.success(this.isEditMode ? '更新成功' : '添加成功');
              this.dialogVisible = false;
              this.fetchCategoryList();
            } else {
              ElMessage.error(data.error || '操作失败');
            }
          } catch (error) {
            const errorMsg = error.response?.data?.error || error.message || '操作失败';
            ElMessage.error(`操作失败: ${errorMsg}`);
            console.error('表单提交错误:', error);
          }
        }
      });
    },

    // 删除分类
    async handleDelete(id) {
      try {
        await ElMessageBox.confirm('确定要删除该分类吗？删除后无法恢复！', '警告', {
          confirmButtonText: '确认删除',
          cancelButtonText: '取消',
          type: 'warning'
        });

        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/account_category_management.php`,
          {
            action: 'delete',
            id: id,
            store_id: this.userInfo.id
          }
        );

        if (data.success) {
          ElMessage.success('删除成功');
          this.fetchCategoryList();
        } else {
          ElMessage.error(data.error || '删除失败');
        }
      } catch (error) {
        if (error !== 'cancel') {
          const errorMsg = error.response?.data?.error || error.message || '删除失败';
          ElMessage.error(`删除失败: ${errorMsg}`);
          console.error('删除错误:', error);
        }
      }
    }
  }
}
</script>

<style scoped lang="scss">
.account-category-page {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
}

.settings-card {
  background: rgba(18, 14, 33, 0.6);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  border: 1px solid rgba(157, 78, 221, 0.2);
  padding: 24px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 16px;
  }

  @media (max-width: 576px) {
    padding: 12px;
  }
}

.page-title {
  font-size: 20px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    color: #c084fc;
  }

  @media (max-width: 768px) {
    font-size: 18px;
    margin-bottom: 16px;
  }

  @media (max-width: 576px) {
    font-size: 16px;
    margin-bottom: 12px;
  }
}

.search-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 20px;

  .search-left {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    flex: 1;
  }

  .search-input {
    flex: 1;
    min-width: 200px;
    
    @media (max-width: 576px) {
      min-width: auto;
      width: 100% !important;
    }
  }

  .search-btn {
    flex-shrink: 0;
  }

  .add-btn {
    flex-shrink: 0;
    
    @media (max-width: 576px) {
      width: 100%;
    }
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;

    .search-left {
      width: 100%;
      flex-wrap: wrap;
      
      .el-input {
        flex: 1;
        min-width: 150px;
      }
    }

    .add-btn {
      width: 100%;
    }
  }
  
  @media (max-width: 576px) {
    gap: 12px;
    
    .search-left {
      flex-direction: column;
      
      .el-input {
        width: 100% !important;
      }
      
      .search-btn {
        width: 100%;
      }
    }
  }
}

/* 按钮文字在小屏幕隐藏 */
@media (max-width: 480px) {
  .btn-text {
    display: none;
  }
  
  .search-btn .btn-text,
  .add-btn .btn-text {
    display: inline;
  }
}

/* 表格包装器 - 实现横向滚动 */
.table-wrapper {
  position: relative;
  width: 100%;
  overflow-x: auto;
  margin-top: 20px;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(157, 78, 221, 0.1);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(157, 78, 221, 0.4);
    border-radius: 4px;

    &:hover {
      background: rgba(157, 78, 221, 0.6);
    }
  }
}

/* 表格样式 - 完全覆盖 Element Plus 默认样式 */
:deep(.el-table) {
  --el-table-bg-color: transparent !important;
  --el-table-tr-bg-color: transparent !important;
  --el-table-header-bg-color: transparent !important;
  --el-table-row-hover-bg-color: rgba(157, 78, 221, 0.1) !important;
  --el-table-current-row-bg-color: rgba(157, 78, 221, 0.15) !important;
  --el-table-stripe-row-bg-color: rgba(157, 78, 221, 0.05) !important;
  --el-table-border-color: rgba(157, 78, 221, 0.2) !important;

  background-color: transparent !important;
  color: #e0e0e0;

  &::before {
    display: none;
  }

  // 表头行
  .el-table__header-wrapper {
    background-color: transparent !important;

    tr {
      background-color: transparent !important;

      // 普通表头（排除固定列）
      th:not(.el-table-fixed-column--right) {
        background: rgba(157, 78, 221, 0.2) !important;
        color: #fff !important;
        font-weight: 600;
        border-bottom: 1px solid rgba(157, 78, 221, 0.3) !important;

        .cell {
          color: #fff !important;
        }
      }
    }
  }

  // 表头（旧版兼容，排除固定列）
  .el-table__header {
    th:not(.el-table-fixed-column--right) {
      background: rgba(157, 78, 221, 0.2) !important;
      color: #fff !important;
    }
  }

  // 表体
  .el-table__body-wrapper {
    background-color: transparent !important;

    tr {
      background-color: transparent !important;

      // 普通 td（排除固定列）
      td:not(.el-table-fixed-column--right) {
        background-color: transparent !important;
        border-bottom: 1px solid rgba(157, 78, 221, 0.1);
        color: #e0e0e0;
      }

      // 斑马纹（排除固定列）
      &.el-table__row--striped td:not(.el-table-fixed-column--right) {
        background-color: rgba(157, 78, 221, 0.05) !important;
      }

      // 悬停（排除固定列）
      &:hover td:not(.el-table-fixed-column--right) {
        background-color: rgba(157, 78, 221, 0.1) !important;
      }
    }
  }

  // 空数据
  .el-table__empty-block {
    background-color: transparent !important;

    .el-table__empty-text {
      color: #a0a0c0 !important;
    }
  }

  // 展开行
  .el-table__expanded-cell {
    background-color: transparent !important;
  }

  // 加载遮罩
  .el-loading-mask {
    background-color: rgba(18, 14, 33, 0.8);
  }
}

/* ========== 固定列（操作列）背景不透明修复 ========== */
:deep(.el-table-fixed-column--right) {
  background-color: #120e21 !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}

// 表头固定列
:deep(th.el-table-fixed-column--right) {
  background-color: #2a1f45 !important;
}

// 斑马纹行的固定列
:deep(.el-table__row--striped .el-table-fixed-column--right) {
  background-color: #1a1430 !important;
}

// hover 行的固定列（已删除，防止滚动条遮挡最后一行时变透明）

.category-table {
  width: 100%;
}

.info-text {
  color: #4ade80;
  font-size: 13px;
}

.warning-text {
  color: #fbbf24;
  font-size: 13px;
}

.empty-text {
  color: #7a7a9a;
}

.notes-text {
  color: #a0a0c0;
  font-size: 13px;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  font-size: 12px;
  border-radius: 8px;
  margin: 0 4px;
  cursor: pointer;
  transition: all 0.2s;
  border: none;

  &.edit-btn {
    background: rgba(157, 78, 221, 0.15);
    border: 1px solid rgba(157, 78, 221, 0.4);
    color: #c084fc;

    &:hover {
      background: rgba(157, 78, 221, 0.25);
      border-color: rgba(157, 78, 221, 0.6);
    }
  }

  &.delete-btn {
    background: rgba(239, 68, 68, 0.15);
    border: 1px solid rgba(239, 68, 68, 0.4);
    color: #f87171;

    &:hover {
      background: rgba(239, 68, 68, 0.25);
      border-color: rgba(239, 68, 68, 0.6);
    }
  }
  
  @media (max-width: 576px) {
    padding: 4px 8px;
    
    .btn-text {
      display: none;
    }
  }
}

/* 分页样式 */
.pagination-container {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;

  @media (max-width: 768px) {
    justify-content: center;
    margin-top: 20px;
  }
  
  @media (max-width: 576px) {
    margin-top: 16px;
  }
}

:deep(.el-pagination) {
  --el-pagination-bg-color: transparent;
  --el-pagination-button-bg-color: rgba(157, 78, 221, 0.1);
  --el-pagination-hover-color: #c084fc;

  .btn-prev,
  .btn-next,
  .el-pager li {
    background: rgba(157, 78, 221, 0.1);
    border: 1px solid rgba(157, 78, 221, 0.3);
    color: #a0a0c0;
    border-radius: 8px;
    min-width: 32px;
    height: 32px;

    &:hover {
      background: rgba(157, 78, 221, 0.2);
      color: #c084fc;
    }

    &.active {
      background: linear-gradient(135deg, #6546b4, #9d4edd);
      color: #fff;
      border-color: transparent;
    }
  }

  .el-pagination__total,
  .el-pagination__jump {
    color: #a0a0c0;
  }

  .el-select .el-input__wrapper {
    background: rgba(157, 78, 221, 0.1);
    border-color: rgba(157, 78, 221, 0.3);
  }
  
  @media (max-width: 576px) {
    .btn-prev,
    .btn-next,
    .el-pager li {
      min-width: 28px;
      height: 28px;
      margin: 0 2px;
    }
    
    .el-pagination__total,
    .el-pagination__jump {
      font-size: 12px;
    }
  }
}

/* 对话框样式 */
:deep(.custom-dialog) {
  --el-dialog-bg-color: rgba(18, 14, 33, 0.98);
  --el-dialog-border-radius: 20px;

  .el-dialog {
    margin: 0 auto !important;
    
    @media (max-width: 576px) {
      .el-dialog__header {
        padding: 16px;
      }
      
      .el-dialog__body {
        padding: 16px;
      }
      
      .el-dialog__footer {
        padding: 12px 16px;
      }
    }
  }

  .el-dialog__header {
    border-bottom: 1px solid rgba(157, 78, 221, 0.2);
    padding: 20px 24px;

    .el-dialog__title {
      color: #fff;
      font-weight: 600;
      font-size: 18px;

      @media (max-width: 768px) {
        font-size: 16px;
      }
    }
  }

  .el-dialog__body {
    padding: 24px;
  }

  .el-dialog__footer {
    border-top: 1px solid rgba(157, 78, 221, 0.2);
    padding: 16px 24px;
  }
}

/* 表单样式 */
:deep(.el-form-item__label) {
  color: #a0a0c0;
  font-weight: 500;

  @media (max-width: 768px) {
    font-size: 13px;
  }
  
  @media (max-width: 576px) {
    font-size: 12px;
  }
}

:deep(.el-input__wrapper) {
  background: rgba(26, 21, 53, 0.8);
  border: 1px solid rgba(157, 78, 221, 0.3);
  border-radius: 10px;
  box-shadow: none;

  &:hover {
    border-color: rgba(157, 78, 221, 0.6);
  }

  &.is-focus {
    border-color: #9d4edd;
    box-shadow: 0 0 0 2px rgba(157, 78, 221, 0.2);
  }

  .el-input__inner {
    color: #fff;
    background: transparent;

    &::placeholder {
      color: #5a5a7a;
    }
  }
}

:deep(.el-textarea__inner) {
  background: rgba(26, 21, 53, 0.8);
  border: 1px solid rgba(157, 78, 221, 0.3);
  border-radius: 10px;
  color: #fff;

  &:hover {
    border-color: rgba(157, 78, 221, 0.6);
  }

  &:focus {
    border-color: #9d4edd;
    box-shadow: 0 0 0 2px rgba(157, 78, 221, 0.2);
  }

  &::placeholder {
    color: #5a5a7a;
  }
}

.input-tip {
  font-size: 11px;
  color: #7a7a9a;
  margin-top: 4px;
}

/* 按钮样式 */
:deep(.el-button) {
  border-radius: 10px;
  padding: 10px 20px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;

  @media (max-width: 768px) {
    padding: 8px 16px;
    font-size: 13px;
  }
}

:deep(.el-button--primary) {
  background: linear-gradient(135deg, #6546b4, #9d4edd);
  border: none;

  &:hover {
    background: linear-gradient(135deg, #7556c4, #ad5eed);
    transform: translateY(-1px);
    box-shadow: 0 4px 15px rgba(157, 78, 221, 0.4);
  }
}

:deep(.el-button--success) {
  background: linear-gradient(135deg, #10b981, #34d399);
  border: none;

  &:hover {
    background: linear-gradient(135deg, #059669, #10b981);
    transform: translateY(-1px);
  }
}

.dialog-btn {
  padding: 10px 20px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  margin-left: 12px;

  @media (max-width: 768px) {
    padding: 8px 16px;
    font-size: 13px;
  }

  &.cancel-btn {
    background: rgba(157, 78, 221, 0.15);
    border: 1px solid rgba(157, 78, 221, 0.4);
    color: #a0a0c0;

    &:hover {
      background: rgba(157, 78, 221, 0.25);
      border-color: rgba(157, 78, 221, 0.6);
      color: #c084fc;
    }
  }

  &.confirm-btn {
    background: linear-gradient(135deg, #6546b4, #9d4edd);
    color: #fff;

    &:hover {
      background: linear-gradient(135deg, #7556c4, #ad5eed);
      transform: translateY(-1px);
      box-shadow: 0 4px 15px rgba(157, 78, 221, 0.4);
    }
  }
}

/* 加载动画 */
:deep(.el-loading-mask) {
  background: rgba(18, 14, 33, 0.8);
  backdrop-filter: blur(4px);
}

:deep(.el-loading-spinner .circular) {
  border-color: #9d4edd;
}

:deep(.el-loading-spinner .path) {
  stroke: #9d4edd;
}
</style>